import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "./layout";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { RequireAccess } from "@/features/auth/RequireAccess";
import { LoginPage } from "@/features/auth/LoginPage";
import { HomePage } from "@/features/home/HomePage";

// Secondary routes are code-split into their own chunks (loaded on
// navigation, behind the layout's <Suspense>). The subscriber detail
// page in particular drags in react-hook-form/zod + every action dialog,
// so keeping it out of the initial bundle is worth the round-trip — it's
// also lazy-loaded when the home page renders it inline (see HomePage).
// Login + home stay eager (they're the landing surfaces).
const SubscriberDetailPage = lazy(() =>
  import("@/features/subscribers/SubscriberDetailPage").then((m) => ({ default: m.SubscriberDetailPage })),
);
const OltsPage = lazy(() => import("@/features/fleet/OltsPage").then((m) => ({ default: m.OltsPage })));
const OltIssuesPage = lazy(() =>
  import("@/features/oltissues/OltIssuesPage").then((m) => ({ default: m.OltIssuesPage })),
);
const RadiusIssuesPage = lazy(() =>
  import("@/features/radiusissues/RadiusIssuesPage").then((m) => ({ default: m.RadiusIssuesPage })),
);
const RadiusStatusPage = lazy(() =>
  import("@/features/radiusstatus/RadiusStatusPage").then((m) => ({ default: m.RadiusStatusPage })),
);
const IpPhonePage = lazy(() => import("@/features/ipphone/IpPhonePage").then((m) => ({ default: m.IpPhonePage })));
const CallBlastPage = lazy(() =>
  import("@/features/callblast/CallBlastPage").then((m) => ({ default: m.CallBlastPage })),
);
const BulkSmsPage = lazy(() =>
  import("@/features/bulksms/BulkSmsPage").then((m) => ({ default: m.BulkSmsPage })),
);
const SmsPage = lazy(() => import("@/features/sms/SmsPage").then((m) => ({ default: m.SmsPage })));
const UsersPage = lazy(() => import("@/features/users/UsersPage").then((m) => ({ default: m.UsersPage })));
const BoxChainsPage = lazy(() =>
  import("@/features/boxchains/BoxChainsPage").then((m) => ({ default: m.BoxChainsPage })),
);
const MessengerConfigPage = lazy(() =>
  import("@/features/messenger/MessengerConfigPage").then((m) => ({ default: m.MessengerConfigPage })),
);
const AudienceExportPage = lazy(() =>
  import("@/features/audienceExport/AudienceExportPage").then((m) => ({ default: m.AudienceExportPage })),
);
const CustomerAuditPage = lazy(() =>
  import("@/features/customerAudit/CustomerAuditPage").then((m) => ({ default: m.CustomerAuditPage })),
);
const RadiusPoolsPage = lazy(() =>
  import("@/features/radiusPools/RadiusPoolsPage").then((m) => ({ default: m.RadiusPoolsPage })),
);
const RoutersPage = lazy(() => import("@/features/fleet/RoutersPage").then((m) => ({ default: m.RoutersPage })));
const FiberCapacityPage = lazy(() =>
  import("@/features/fiber/FiberCapacityPage").then((m) => ({ default: m.FiberCapacityPage })),
);
const FiberSchematicPage = lazy(() =>
  import("@/features/fiber/FiberSchematicPage").then((m) => ({ default: m.FiberSchematicPage })),
);
const FiberIssuesPage = lazy(() =>
  import("@/features/fiber/FiberIssuesPage").then((m) => ({ default: m.FiberIssuesPage })),
);
const FiberCablesPage = lazy(() =>
  import("@/features/fiber/FiberCablesPage").then((m) => ({ default: m.FiberCablesPage })),
);
const FiberDPsPage = lazy(() =>
  import("@/features/fiber/FiberDPsPage").then((m) => ({ default: m.FiberDPsPage })),
);
const NetTestReportPage = lazy(() =>
  import("@/features/netTest/ReportPage").then((m) => ({ default: m.ReportPage })),
);
const PppoeSessionsPage = lazy(() =>
  import("@/features/pppoe/PppoeSessionsPage").then((m) => ({ default: m.PppoeSessionsPage })),
);

// basename "/app" matches vite's base — dev (proxied) and the prod /app/
// deploy both live under that prefix. Two-layer guard per protected page:
// <RequireAuth> (logged in?) then <RequireAccess permission=…> (allowed?).
// Add children here (or split per-feature route files and merge) — and
// give each gated page its permission, matching nav.ts.
//
// `/` is the search home (subscriber lookup + the live network tile
// strip) — the React port of the PHP portal's index.php. `/dashboard`
// and `/subscribers` redirect to it (the standalone fleet-overview and
// search pages folded into one home).
export const router = createBrowserRouter(
  [
    { path: "/login", element: <LoginPage /> },
    {
      path: "/",
      element: (
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      ),
      children: [
        {
          index: true,
          element: (
            <RequireAccess permission="subscribers:read">
              <HomePage />
            </RequireAccess>
          ),
        },
        { path: "dashboard", element: <Navigate to="/" replace /> },
        { path: "subscribers", element: <Navigate to="/" replace /> },
        {
          path: "subscribers/:username",
          element: (
            <RequireAccess permission="subscribers:read">
              <SubscriberDetailPage />
            </RequireAccess>
          ),
        },
        {
          path: "subscribers/:username/reports/:reportId",
          element: (
            <RequireAccess permission="subscribers:read">
              <NetTestReportPage />
            </RequireAccess>
          ),
        },
        {
          path: "olts",
          element: (
            <RequireAccess permission="olts:read">
              <OltsPage />
            </RequireAccess>
          ),
        },
        {
          path: "olt-issues",
          element: (
            <RequireAccess permission="olts:read">
              <OltIssuesPage />
            </RequireAccess>
          ),
        },
        {
          path: "radius-status",
          element: (
            <RequireAccess permission="radius:operate">
              <RadiusStatusPage />
            </RequireAccess>
          ),
        },
        {
          path: "radius-issues",
          element: (
            <RequireAccess permission="radius:operate">
              <RadiusIssuesPage />
            </RequireAccess>
          ),
        },
        {
          path: "box-chains",
          element: (
            <RequireAccess permission="radius:operate">
              <BoxChainsPage />
            </RequireAccess>
          ),
        },
        {
          path: "fiber-capacity",
          element: (
            <RequireAccess permission="fiber:read">
              <FiberCapacityPage />
            </RequireAccess>
          ),
        },
        {
          path: "fiber-schematic",
          element: (
            <RequireAccess permission="fiber:read">
              <FiberSchematicPage />
            </RequireAccess>
          ),
        },
        {
          path: "fiber-issues",
          element: (
            <RequireAccess permission="fiber:read">
              <FiberIssuesPage />
            </RequireAccess>
          ),
        },
        {
          path: "fiber-cables",
          element: (
            <RequireAccess permission="fiber:read">
              <FiberCablesPage />
            </RequireAccess>
          ),
        },
        {
          path: "fiber-nodes",
          element: (
            <RequireAccess permission="fiber:read">
              <FiberDPsPage />
            </RequireAccess>
          ),
        },
        {
          path: "routers",
          element: (
            <RequireAccess permission="routers:read">
              <RoutersPage />
            </RequireAccess>
          ),
        },
        {
          path: "pppoe-sessions",
          element: (
            <RequireAccess permission="subscribers:read">
              <PppoeSessionsPage />
            </RequireAccess>
          ),
        },
        {
          path: "ip-phone",
          element: (
            <RequireAccess permission="ipphone:read">
              <IpPhonePage />
            </RequireAccess>
          ),
        },
        {
          path: "call-blast",
          element: (
            <RequireAccess permission="callblast:read">
              <CallBlastPage />
            </RequireAccess>
          ),
        },
        {
          path: "send-sms",
          element: (
            <RequireAccess permission="bulksms:send">
              <BulkSmsPage />
            </RequireAccess>
          ),
        },
        {
          path: "sms",
          element: (
            <RequireAccess permission="sms:read">
              <SmsPage />
            </RequireAccess>
          ),
        },
        {
          path: "users",
          element: (
            <RequireAccess permission="admin:config">
              <UsersPage />
            </RequireAccess>
          ),
        },
        {
          path: "messenger",
          element: (
            <RequireAccess permission="admin:config">
              <MessengerConfigPage />
            </RequireAccess>
          ),
        },
        {
          path: "audience-export",
          element: (
            <RequireAccess permission="admin:config">
              <AudienceExportPage />
            </RequireAccess>
          ),
        },
        {
          path: "radius-pools",
          element: (
            <RequireAccess permission="admin:config">
              <RadiusPoolsPage />
            </RequireAccess>
          ),
        },
        {
          path: "customer-audit",
          element: (
            <RequireAccess permission="admin:config">
              <CustomerAuditPage />
            </RequireAccess>
          ),
        },
      ],
    },
    { path: "*", element: <Navigate to="/" replace /> },
  ],
  { basename: "/app" },
);
